import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IProject, IProjectPhase } from '@/types/project';
import { LinkedProduct } from '@/services/bomService';
import NetlistImportDialog from '../NetlistImportDialog';

interface PreBOMPhasePanelProps {
  project: IProject;
  phase: IProjectPhase;
  linkedProduct?: LinkedProduct | null;
  onCreateProduct?: () => void;
  onLinkProduct?: () => void;
  onRefresh?: () => void;
}

const PreBOMPhasePanel: React.FC<PreBOMPhasePanelProps> = ({
  project,
  phase,
  linkedProduct,
  onCreateProduct,
  onLinkProduct,
  onRefresh,
}) => {
  const { t } = useTranslation('project');
  const navigate = useNavigate();
  const [showNetlistImport, setShowNetlistImport] = useState(false);
  // Phase prop will be used for future phase-specific logic
  void phase;

  const handleViewBOM = () => {
    if (linkedProduct) {
      navigate(`/app/bom/products/${linkedProduct.product_id}`);
    }
  };

  const handleNetlistImportComplete = () => {
    setShowNetlistImport(false);
    onRefresh?.();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('phases.preBomActions')}
        </h3>
        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          PRE-BOM
        </span>
      </div>

      {/* BOM Product Status */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('phases.bomProduct')}
        </label>

        {linkedProduct ? (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {linkedProduct.product_name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {linkedProduct.product_code} | Rev. {linkedProduct.current_revision}
                </p>
              </div>
              <button
                onClick={handleViewBOM}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                {t('phases.viewBom')}
              </button>
            </div>

            {/* BOM Summary */}
            <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">--</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('phases.totalItems')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">--</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('phases.tbdParts')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">--</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('phases.matched')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t('phases.noBomLinked')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onCreateProduct}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                {t('phases.createBomProduct')}
              </button>
              <button
                onClick={onLinkProduct}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {t('phases.linkExisting')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Netlist Import Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('phases.netlistImport')}
        </label>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                {t('phases.netlistImportDescription')}
              </p>
              <button
                onClick={() => setShowNetlistImport(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {t('phases.importKicadNetlist')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Netlist Import Dialog */}
      {showNetlistImport && (
        <NetlistImportDialog
          project={project}
          linkedProduct={linkedProduct}
          onClose={() => setShowNetlistImport(false)}
          onComplete={handleNetlistImportComplete}
        />
      )}
    </div>
  );
};

export default PreBOMPhasePanel;
